// Superchat Spec 01 — the host's production queue (Host Controls). Superchats,
// highest amount first, each with a Feature button that puts it on-air as the
// read-aloud lower-third. The currently-featured row shows an On-air state and a
// Clear button. One featured at a time — enforced by state, not by disabling.
//
// Only mounts inside the host/cohost wrapper, so being on screen already means
// publisher. Reads the derived `superchats` from useSocialChat and drives the
// on-air graphic through the shared FeaturedSuperchat context.

import { useRoomContext } from '@livekit/components-react';

import { useFeaturedSuperchatContext } from './useFeaturedSuperchat';
import { useSocialChat } from './useSocialChat';

import type { SuperchatMessage } from '@archetelos/lib/live';

export function SuperchatQueue() {
  const room = useRoomContext();
  const { superchats } = useSocialChat(room);
  const { featured, featureSuperchat, clearFeatured } = useFeaturedSuperchatContext();

  return (
    <div className="space-y-3">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
        Superchats
      </p>
      <p className="font-sans text-xs text-video-text-faint">
        Highest first. Feature one to put it on air for read-aloud.
      </p>

      <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-video-line bg-video-inset p-3">
        {superchats.length === 0 ? (
          <p className="font-sans text-xs text-video-text-faint">No superchats yet.</p>
        ) : (
          superchats.map((sc) => (
            <QueueRow
              key={sc.id}
              sc={sc}
              onAir={featured?.id === sc.id}
              onFeature={() => featureSuperchat(sc)}
              onClear={clearFeatured}
            />
          ))
        )}
      </div>
    </div>
  );
}

function QueueRow({
  sc,
  onAir,
  onFeature,
  onClear,
}: {
  sc: SuperchatMessage;
  onAir: boolean;
  onFeature: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-start gap-2 border-l-2 border-superchat/70 pl-2">
      <div className="min-w-0 flex-1">
        <p className="font-sans text-sm leading-snug">
          <span className="font-semibold text-superchat">{sc.amount.display}</span>
          {sc.amount.currency ? (
            <span className="ml-1 rounded-sm bg-superchat/15 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-superchat-muted">
              {sc.amount.currency}
            </span>
          ) : null}{' '}
          <span className="font-medium text-video-text">{sc.author.name}</span>
        </p>
        {sc.text ? (
          <p className="truncate font-sans text-xs text-video-text-muted">{sc.text}</p>
        ) : null}
      </div>
      {onAir ? (
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 rounded-md bg-superchat px-3 py-1 font-sans text-xs font-medium text-video-bg"
        >
          On air · Clear
        </button>
      ) : (
        <button
          type="button"
          onClick={onFeature}
          className="shrink-0 rounded-md border border-superchat/60 px-3 py-1 font-sans text-xs text-superchat hover:bg-superchat/10"
        >
          Feature
        </button>
      )}
    </div>
  );
}
